/**
 * Lead Pipeline Registry Parser
 * 
 * Parses the consolidated Markdown registry file (content/lead-pipeline.md)
 * and provides typed access to pipeline stages, outcomes, and metadata.
 * 
 * This is the SINGLE SOURCE OF TRUTH for the Lead Pipeline.
 */

import yaml from 'js-yaml';
import type {
  LeadPipelineRegistry,
  PipelineStageRegistry,
  PipelineOutcome,
  PipelineStageMeta,
  CRMField,
  ReasonCode,
  PipelineTemplate,
  ExceptionPlaybook,
  HandoverRule,
  ValidationRule,
  StageActivity,
  ActivityStep,
  PipelineBrand,
} from '../types';

// Import the raw markdown content
import pipelineMarkdown from '../../content/lead-pipeline.md?raw';

/**
 * Parse YAML block from fenced code block
 */
function parseYamlBlock(content: string): Record<string, unknown> | null {
  const yamlMatch = content.match(/```yaml\n([\s\S]*?)\n```/);
  if (!yamlMatch) return null;
  
  try {
    return yaml.load(yamlMatch[1]) as Record<string, unknown>;
  } catch (e) {
    console.error('Failed to parse YAML block:', e);
    return null;
  }
}

/**
 * Parse a markdown section into an array of bullet points
 */
function parseBulletList(content: string): string[] {
  const lines = content.split('\n');
  const items: string[] = [];
  
  for (const line of lines) {
    const match = line.match(/^[-*]\s+(.+)$/);
    if (match) {
      items.push(match[1].trim());
    }
  }
  
  return items;
}

/**
 * Parse a markdown table into an array of objects
 */
function parseTable(content: string): Record<string, string>[] {
  const lines = content.split('\n').filter(line => line.trim().startsWith('|'));
  if (lines.length < 2) return [];
  
  // Parse header row
  const headers = lines[0]
    .split('|')
    .filter(cell => cell.trim())
    .map(cell => cell.trim().toLowerCase().replace(/\s+/g, '_'));
  
  // Skip separator row, parse data rows
  const rows: Record<string, string>[] = [];
  for (let i = 2; i < lines.length; i++) {
    const cells = lines[i]
      .split('|')
      .filter(cell => cell.trim())
      .map(cell => cell.trim());
    
    if (cells.length === headers.length) {
      const row: Record<string, string> = {};
      headers.forEach((header, idx) => {
        row[header] = cells[idx];
      });
      rows.push(row);
    }
  }
  
  return rows;
}

/**
 * Extract section content by heading
 */
function extractSection(content: string, heading: string): string {
  const headingPattern = new RegExp(`### ${heading}[\\s\\S]*?(?=###|$)`, 'i');
  const match = content.match(headingPattern);
  if (!match) return '';
  
  // Remove the heading line itself
  return match[0].replace(new RegExp(`### ${heading}.*\\n`, 'i'), '').trim();
}

/**
 * Parse templates from the Templates section
 */
function parseTemplates(content: string): PipelineTemplate[] {
  const templatesSection = extractSection(content, 'Templates');
  if (!templatesSection) return [];
  
  const templates: PipelineTemplate[] = [];
  
  // Match template blocks: **Title** followed by code block
  const templatePattern = /\*\*([^*]+)\*\*\s*\n```\n([\s\S]*?)\n```/g;
  let match;
  
  while ((match = templatePattern.exec(templatesSection)) !== null) {
    const title = match[1].trim();
    const templateContent = match[2];
    
    // Determine channel and language from title
    let channel: 'WhatsApp' | 'SMS' | 'Internal' = 'WhatsApp';
    let language: 'en' | 'ar' | 'both' = 'en';
    
    if (title.toLowerCase().includes('sms')) channel = 'SMS';
    if (title.toLowerCase().includes('internal')) channel = 'Internal';
    if (title.toLowerCase().includes('arabic') || title.includes('عربي')) language = 'ar';
    if (title.toLowerCase().includes('english')) language = 'en';
    
    templates.push({
      title,
      channel,
      language,
      content: templateContent,
    });
  }
  
  return templates;
}

/**
 * Parse exception playbooks
 */
function parseExceptionPlaybooks(content: string): ExceptionPlaybook[] {
  const section = extractSection(content, 'Exceptions & Recovery Playbooks');
  if (!section) return [];
  
  const playbooks: ExceptionPlaybook[] = [];
  
  // Match exception blocks: **Exception: Title** followed by content
  const exceptionPattern = /\*\*Exception:\s*([^*]+)\*\*\s*\n([\s\S]*?)(?=\*\*Exception:|$)/g;
  let match;
  
  while ((match = exceptionPattern.exec(section)) !== null) {
    const title = match[1].trim();
    const exceptionContent = match[2].trim();
    
    // Parse trigger and actions
    const triggerMatch = exceptionContent.match(/Trigger:\s*(.+)/i);
    const actions = parseBulletList(exceptionContent)
      .filter(item => !item.toLowerCase().startsWith('trigger:'));
    
    playbooks.push({
      title,
      trigger: triggerMatch ? triggerMatch[1] : '',
      actions,
    });
  }
  
  return playbooks;
}

/**
 * Parse reason codes from a table in content
 */
function parseReasonCodes(content: string): ReasonCode[] {
  const rows = parseTable(content);
  return rows.map(row => ({
    code: row.code || '',
    label: row.label || '',
    labelAr: row.label_arabic || row['label_(arabic)'] || '',
    description: row.when_to_use || row.description || '',
  })).filter(rc => rc.code);
}

/**
 * Parse mandatory CRM fields from a table
 */
function parseCRMFields(content: string): CRMField[] {
  const section = extractSection(content, 'Mandatory CRM Fields');
  if (!section) return [];
  
  const rows = parseTable(section);
  return rows.map(row => ({
    field: row.field || '',
    type: row.type || '',
    required: row.required === 'Yes' || row.required === 'System' ? true : row.required || false,
    validation: row.validation,
  })).filter(f => f.field);
}

/**
 * Parse validation rules
 */
function parseValidationRules(content: string): ValidationRule[] {
  const section = extractSection(content, 'Validation Rules');
  if (!section) return [];
  
  const rules: ValidationRule[] = [];
  const lines = section.split('\n');
  
  for (const line of lines) {
    const match = line.match(/^[-*]\s+\*\*([^*]+)\*\*:\s*(.+)$/);
    if (match) {
      rules.push({
        rule: match[1].trim(),
        description: match[2].trim(),
      });
    }
  }
  
  return rules;
}

/**
 * Parse handover rules
 */
function parseHandoverRules(content: string): HandoverRule[] {
  const section = extractSection(content, 'Handover Rules');
  if (!section) return [];
  
  const rules: HandoverRule[] = [];
  const lines = section.split('\n');
  
  for (const line of lines) {
    // Match patterns like "- **From → To**: description"
    const match = line.match(/^[-*]\s+\*\*([^→]+)→([^*]+)\*\*:\s*(.+)$/);
    if (match) {
      rules.push({
        from: match[1].trim(),
        to: match[2].trim(),
        trigger: '',
        description: match[3].trim(),
      });
    }
  }
  
  return rules;
}

/**
 * Parse activities section (e.g., Test Drive in Showroom Engagement)
 */
function parseActivities(content: string): StageActivity[] {
  const activitiesSection = extractSection(content, 'Activities');
  if (!activitiesSection) return [];
  
  const activities: StageActivity[] = [];
  
  // Look for activity headers (#### Activity Name)
  const activityPattern = /####\s+([^\n]+)\s*Activity\s*\n([\s\S]*?)(?=####|###|$)/gi;
  let match;
  
  while ((match = activityPattern.exec(activitiesSection)) !== null) {
    const title = match[1].trim();
    const activityContent = match[2];
    
    // Parse steps from table
    const stepsTable = parseTable(activityContent);
    const steps: ActivityStep[] = stepsTable.map((row, idx) => ({
      step: parseInt(row.step) || idx + 1,
      owner: row.owner || '',
      action: row.action || '',
      crmUpdate: row.crm_update || row.documentation || '',
    }));
    
    activities.push({
      id: title.toLowerCase().replace(/\s+/g, '_'),
      title: title + ' Activity',
      titleAr: '', // Would need to be added to markdown
      owner: '', // Would need to be extracted
      ownerAr: '',
      description: '',
      descriptionAr: '',
      steps,
    });
  }
  
  return activities;
}

/**
 * Parse a single stage section from markdown
 */
function parseStageSection(sectionContent: string): PipelineStageRegistry | null {
  // Parse YAML metadata
  const yamlData = parseYamlBlock(sectionContent);
  if (!yamlData || !yamlData.meta) return null;
  
  const metaRaw = yamlData.meta as Record<string, unknown>;
  
  const meta: PipelineStageMeta = {
    id: String(metaRaw.id || ''),
    type: (metaRaw.type as 'pre-stage' | 'stage') || 'stage',
    order: Number(metaRaw.order) || 0,
    title: String(metaRaw.title || ''),
    titleAr: String(metaRaw.titleAr || ''),
    owner: String(metaRaw.owner || ''),
    ownerAr: String(metaRaw.ownerAr || ''),
    sla: String(metaRaw.sla || ''),
    slaAr: String(metaRaw.slaAr || ''),
    color: String(metaRaw.color || '#6366f1'),
    appliesToBrands: (metaRaw.appliesToBrands as PipelineBrand[]) || ['Changan', 'Hyundai', 'Stellantis'],
    pilotBrand: (metaRaw.pilotBrand as PipelineBrand) || 'Changan',
  };
  
  // Parse text sections
  const description = extractSection(sectionContent, 'Description')
    .split('\n')
    .filter(line => !line.startsWith('###'))
    .join('\n')
    .trim();
  
  const descriptionAr = extractSection(sectionContent, 'Description \\(Arabic\\)');
  
  // Parse lists
  const entryCriteria = parseBulletList(extractSection(sectionContent, 'Entry Criteria'));
  const exitCriteria = parseBulletList(extractSection(sectionContent, 'Exit Criteria'));
  const actions = parseBulletList(extractSection(sectionContent, 'Actions'));
  const automations = parseBulletList(extractSection(sectionContent, 'Automations'));
  const kpis = parseBulletList(extractSection(sectionContent, 'KPIs'));
  
  // Parse structured data
  const mandatoryFields = parseCRMFields(sectionContent);
  const validationRules = parseValidationRules(sectionContent);
  const exceptionPlaybooks = parseExceptionPlaybooks(sectionContent);
  const templates = parseTemplates(sectionContent);
  const handoverRules = parseHandoverRules(sectionContent);
  const activities = parseActivities(sectionContent);
  
  // Parse reason codes
  const reasonCodesSection = extractSection(sectionContent, 'Reason Codes');
  const reasonCodes: PipelineStageRegistry['reasonCodes'] = {};
  
  // Check for "For Lost" subsection
  if (reasonCodesSection.includes('For Lost')) {
    const lostSection = reasonCodesSection.match(/\*\*For Lost[^*]*\*\*[\s\S]*?(?=\*\*For|\n\n###|$)/i);
    if (lostSection) {
      reasonCodes.lost = parseReasonCodes(lostSection[0]);
    }
  }
  
  // Check for "For Nurture" subsection
  if (reasonCodesSection.includes('For Nurture')) {
    const nurtureSection = reasonCodesSection.match(/\*\*For Nurture[^*]*\*\*[\s\S]*?(?=\*\*For|\n\n###|$)/i);
    if (nurtureSection) {
      reasonCodes.nurture = parseReasonCodes(nurtureSection[0]);
    }
  }
  
  // Check for "For No-Show" subsection
  if (reasonCodesSection.includes('For No-Show')) {
    const noShowSection = reasonCodesSection.match(/\*\*For No-Show[^*]*\*\*[\s\S]*?(?=\*\*For|\n\n###|$)/i);
    if (noShowSection) {
      reasonCodes.noShow = parseReasonCodes(noShowSection[0]);
    }
  }
  
  // Check for "For Cancellation" subsection
  if (reasonCodesSection.includes('For Cancellation')) {
    const cancellationSection = reasonCodesSection.match(/\*\*For Cancellation[^*]*\*\*[\s\S]*?(?=\*\*For|\n\n###|$)/i);
    if (cancellationSection) {
      reasonCodes.cancellation = parseReasonCodes(cancellationSection[0]);
    }
  }
  
  // General reason codes (no subcategory)
  if (!reasonCodes.lost && !reasonCodes.nurture && reasonCodesSection) {
    const generalCodes = parseReasonCodes(reasonCodesSection);
    if (generalCodes.length > 0) {
      reasonCodes.lost = generalCodes;
    }
  }
  
  return {
    meta,
    description,
    descriptionAr,
    entryCriteria,
    exitCriteria,
    actions,
    automations,
    kpis,
    mandatoryFields,
    validationRules,
    reasonCodes,
    handoverRules,
    exceptionPlaybooks,
    templates,
    activities: activities.length > 0 ? activities : undefined,
  };
}

/**
 * Parse outcomes section from markdown
 */
function parseOutcomes(content: string): PipelineOutcome[] {
  const outcomesMatch = content.match(/## OUTCOMES[\s\S]*?(?=## PRE-STAGE|## STAGE)/i);
  if (!outcomesMatch) return [];
  
  const yamlData = parseYamlBlock(outcomesMatch[0]);
  if (!yamlData || !yamlData.outcomes) return [];
  
  const outcomesRaw = yamlData.outcomes as Array<Record<string, unknown>>;
  
  return outcomesRaw.map(outcome => ({
    id: String(outcome.id || ''),
    title: String(outcome.title || ''),
    titleAr: String(outcome.titleAr || ''),
    description: String(outcome.description || ''),
    requiresReasonCode: Boolean(outcome.requiresReasonCode),
  }));
}

/**
 * Main parser function - parses the complete registry
 */
function parseLeadPipelineRegistry(markdown: string): LeadPipelineRegistry {
  // Parse outcomes
  const outcomes = parseOutcomes(markdown);
  
  // Split into stage sections
  const preStageMatch = markdown.match(/## PRE-STAGE[^#][\s\S]*?(?=## STAGE 1:|$)/i);
  const stageMatches = markdown.matchAll(/## STAGE (\d+):[^#][\s\S]*?(?=## STAGE \d+:|## End of|# End of|$)/gi);
  
  let preStage: PipelineStageRegistry | null = null;
  const stages: PipelineStageRegistry[] = [];
  
  // Parse pre-stage
  if (preStageMatch) {
    preStage = parseStageSection(preStageMatch[0]);
  }
  
  // Parse each numbered stage
  for (const match of stageMatches) {
    const stage = parseStageSection(match[0]);
    if (stage) {
      stages.push(stage);
    }
  }
  
  // Sort stages by order
  stages.sort((a, b) => a.meta.order - b.meta.order);
  
  // Extract version info
  const versionMatch = markdown.match(/\*\*Document Version\*\*:\s*([\d.]+)/i);
  const lastUpdatedMatch = markdown.match(/\*\*Last Updated\*\*:\s*([\d-]+)/i);
  
  return {
    version: versionMatch ? versionMatch[1] : '1.0.0',
    lastUpdated: lastUpdatedMatch ? lastUpdatedMatch[1] : new Date().toISOString().split('T')[0],
    preStage: preStage!,
    stages,
    outcomes,
  };
}

// Parse the registry on module load
let cachedRegistry: LeadPipelineRegistry | null = null;

/**
 * Get the parsed Lead Pipeline Registry
 */
export function getLeadPipelineRegistry(): LeadPipelineRegistry {
  if (!cachedRegistry) {
    cachedRegistry = parseLeadPipelineRegistry(pipelineMarkdown);
  }
  return cachedRegistry;
}

/**
 * Get a specific stage by ID
 */
export function getStageById(stageId: string): PipelineStageRegistry | undefined {
  const registry = getLeadPipelineRegistry();
  
  // Check pre-stage
  if (registry.preStage?.meta.id === stageId) {
    return registry.preStage;
  }
  
  // Check stages
  return registry.stages.find(stage => stage.meta.id === stageId);
}

/**
 * Get a specific stage by order number
 */
export function getStageByOrder(order: number): PipelineStageRegistry | undefined {
  const registry = getLeadPipelineRegistry();
  
  if (order === 0) {
    return registry.preStage;
  }
  
  return registry.stages.find(stage => stage.meta.order === order);
}

/**
 * Get all stages (including pre-stage) sorted by order
 */
export function getAllStages(): PipelineStageRegistry[] {
  const registry = getLeadPipelineRegistry();
  const allStages: PipelineStageRegistry[] = [];
  
  if (registry.preStage) {
    allStages.push(registry.preStage);
  }
  
  allStages.push(...registry.stages);
  
  return allStages.sort((a, b) => a.meta.order - b.meta.order);
}

/**
 * Get only CRM stages (excludes pre-stage)
 */
export function getCRMStages(): PipelineStageRegistry[] {
  const registry = getLeadPipelineRegistry();
  return registry.stages.sort((a, b) => a.meta.order - b.meta.order);
}

/**
 * Get the pre-stage
 */
export function getPreStage(): PipelineStageRegistry | undefined {
  const registry = getLeadPipelineRegistry();
  return registry.preStage;
}

/**
 * Get all outcomes
 */
export function getOutcomes(): PipelineOutcome[] {
  const registry = getLeadPipelineRegistry();
  return registry.outcomes;
}

/**
 * Filter stages by brand applicability
 */
export function getStagesForBrand(brand: PipelineBrand): PipelineStageRegistry[] {
  const allStages = getAllStages();
  return allStages.filter(stage => stage.meta.appliesToBrands.includes(brand));
}

/**
 * Get count of CRM stages (excluding pre-stage)
 */
export function getCRMStageCount(): number {
  const registry = getLeadPipelineRegistry();
  return registry.stages.length;
}

/**
 * Export the raw registry for debugging
 */
export const registry = getLeadPipelineRegistry();

export default registry;
